import {
  AudioLines,
  Ban,
  Cable,
  Gauge,
  LayoutDashboard,
  ListMusic,
  Menu,
  Mic2,
  Settings,
  SlidersHorizontal,
  X,
  type LucideProps,
} from 'lucide-solid';
import { createEffect, createMemo, createSignal, For, onCleanup, Show, type Component, type JSX } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { can } from '../lib/permissions.generated';
import { OutputViewIndicator } from './OutputViewIndicator';
import type { RouteName } from '../lib/types';
import { roomOptionValue } from '../lib/roomOption';
import { useVenue } from '../state/VenueProvider';
import { BusyButton, ConfirmModal, ToastStack } from './ui';
import { TEST_IDS } from '../testIds';

interface NavigationItem {
  route: RouteName;
  label: string;
  description: string;
  icon: Component<LucideProps>;
  primary?: boolean;
}

export const navigation: NavigationItem[] = [
  { route: 'scenes', label: 'Scenes', description: 'Room selection and scenes', icon: SlidersHorizontal, primary: true },
  { route: 'scores', label: 'Scores', description: 'Playback', icon: ListMusic, primary: true },
  { route: 'effects', label: 'Effects', description: 'Room-wide looks', icon: AudioLines, primary: true },
  { route: 'music', label: 'Music', description: 'Inputs and live analysis', icon: Mic2, primary: true },
  { route: 'status', label: 'Status', description: 'Controller health', icon: Gauge },
  { route: 'settings', label: 'Settings', description: 'Output and account', icon: Settings },
  { route: 'connect', label: 'Connect', description: 'Rooms and controllers', icon: Cable },
];

export function AppShell(props: { children: JSX.Element }): JSX.Element {
  const venue = useVenue();
  const [mobileMenu, setMobileMenu] = createSignal(false);
  const [blackoutConfirmOpen, setBlackoutConfirmOpen] = createSignal(false);
  const [streamConfirmOpen, setStreamConfirmOpen] = createSignal(false);
  const [streamObservedAt, setStreamObservedAt] = createSignal(Date.now());
  const [now, setNow] = createSignal(Date.now());
  const active = createMemo(() => navigation.find((item) => item.route === venue.route()));
  /** Only the two direct routes are ever named; see the header below for why. */
  const connectionLabel = createMemo(() => venue.controlTransport() === 'bluetooth' ? 'Bluetooth' : 'Venue network');
  const roomChoice = createMemo(() => roomOptionValue(venue.selectedRoom()?.roomId ?? '', venue.selectedRigId()));
  const roomOption = (room: NonNullable<ReturnType<typeof venue.selectedRoom>>, rigId: string): string => {
    if (!rigId) return room.displayName ?? '';
    const rig = room.activeRigs?.find((entry) => entry.rigId === rigId);
    const binding = room.rigs?.find((entry) => entry.rigId === rigId);
    const controllerName = binding?.controllerId ? venue.boundDevice(binding.controllerId)?.displayName : undefined;
    return [room.displayName, rig?.name, controllerName].filter(Boolean).join(' · ');
  };
  const blackout = createMemo(() => venue.controllerState()?.blackout ?? venue.mirror()?.blackout === true);
  const activityText = createMemo(() => {
    const activity = venue.activity();
    if (!activity?.at) return '';
    const age = now() - Date.parse(activity.at);
    if (!Number.isFinite(age) || age < 0 || age > 15_000) return '';
    const label = activity.actor?.label || 'another operator';
    if (activity.phase === 'editing' && age <= 4_000) return `${label} is editing…`;
    if (activity.phase === 'denied') return activity.message ? `Not applied: ${activity.message}` : 'Change not applied';
    if (['committed', 'applied'].includes(activity.phase)) return `Changed by ${label} · just now`;
    return '';
  });
  const clock = globalThis.setInterval(() => setNow(Date.now()), 1_000);
  onCleanup(() => globalThis.clearInterval(clock));

  function go(route: RouteName): void {
    venue.setRoute(route);
    setMobileMenu(false);
    requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: 'instant' }));
  }

  let observedStreamKey = '';
  createEffect(() => {
    const ownership = venue.controlOwnership();
    if (!ownership) {
      observedStreamKey = '';
      return;
    }
    const key = ownership.kind;
    if (key !== observedStreamKey) {
      observedStreamKey = key;
      setStreamObservedAt(Date.now());
    }
  });
  createEffect(() => {
    document.title = `${active()?.label ?? 'Console'} · Nuance Venue`;
  });

  const streamSince = createMemo(() => {
    const state = venue.controllerState();
    const source = state?.stream ?? state?.remoteLive ?? venue.remoteSession();
    const raw = source?.startedAt ?? source?.activeSince ?? source?.since ?? source?.createdAt;
    const reported = typeof raw?.toDate === 'function'
      ? raw.toDate().getTime()
      : Number.isFinite(Number(raw?.seconds))
        ? Number(raw.seconds) * 1_000
        : raw == null
          ? Number.NaN
          : new Date(typeof raw === 'number' ? raw : String(raw)).getTime();
    const date = new Date(Number.isFinite(reported) ? reported : streamObservedAt());
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  });

  async function setBlackout(on: boolean): Promise<void> {
    setBlackoutConfirmOpen(false);
    await venue.runAction(
      'global-blackout',
      () => venue.sendBlackout(on),
      on ? 'Blackout is on' : 'Blackout is off',
    );
  }

  async function disconnectStream(): Promise<void> {
    setStreamConfirmOpen(false);
    await venue.runAction('disconnect-stream', venue.takeControl, 'Stream disconnected');
  }

  return (
    <div
      class="app-shell"
      data-active-source={venue.activeSource()}
      data-active-source-label={venue.activeSourceLabel()}
      data-control-transport={venue.controlTransport()}
    >
      <aside class="sidebar">
        <div class="brand-lockup">
          <img src="/brand/nuance-logo-256.png" alt="" />
          <span>Nuance Venue</span>
        </div>
        <nav aria-label="Console screens">
          <For each={navigation}>
            {(item) => (
              <button classList={{ active: venue.route() === item.route }} onClick={() => go(item.route)} data-route={item.route}>
                <item.icon size={19} strokeWidth={1.8} />
                <span><strong>{item.label}</strong><small>{item.description}</small></span>
              </button>
            )}
          </For>
        </nav>
      </aside>

      <section class="app-main">
        <header class="context-bar">
          <button class="mobile-menu-button" aria-label="Open navigation" onClick={() => setMobileMenu(true)}><Menu size={21} /></button>
          {/*
            * Venue and room, and what these say when there is nothing to name.
            *
            * The fallback option is the whole picker whenever the cloud has returned no list — a brand
            * new account with no venue yet, or a venue with no room. It states that nothing is selected
            * rather than inventing a name for it. The cable console reaches the same fallback with a
            * venue and room it genuinely has, named by the provider from the controller's `hello`, and
            * keeps those names.
            */}
          <div class="context-selectors">
            <label>
              <span>Venue</span>
              <select value={venue.selectedVenue()?.venueId ?? ''} onChange={(event) => void venue.selectVenue(event.currentTarget.value)}>
                <Show when={venue.venues().length} fallback={<option value={venue.selectedVenue()?.venueId ?? ''}>{venue.selectedVenue()?.displayName ?? 'No venue selected'}</option>}>
                  <For each={venue.venues()}>{(item) => <option value={item.venueId} selected={item.venueId === venue.selectedVenue()?.venueId}>{item.displayName}</option>}</For>
                </Show>
              </select>
            </label>
            <span class="context-divider" aria-hidden="true" />
            <label>
              <span>Room</span>
              <select class="room-picker" value={roomChoice()} onChange={(event) => {
                const [roomId, rigId] = event.currentTarget.value.split(':');
                void venue.selectRoom(roomId, rigId);
              }}>
                <Show when={venue.rooms().length} fallback={<option value={roomChoice()}>{venue.selectedRoom()?.displayName ?? 'No room selected'}</option>}>
                  <For each={venue.rooms()}>{(item) => (
                    <Show when={item.rigs?.length} fallback={<option value={roomOptionValue(item.roomId, '')}>{roomOption(item, '')}</option>}>
                      <For each={item.rigs}>{(rig) => (
                        <option value={roomOptionValue(item.roomId, rig.rigId)} selected={item.roomId === venue.selectedRoom()?.roomId && rig.rigId === venue.selectedRigId()}>
                          {roomOption(item, rig.rigId)}
                        </option>
                      )}</For>
                    </Show>
                  )}</For>
                </Show>
              </select>
            </label>
          </div>
          <div class="context-status">
            <span
              class={`control-feedback-slot ${venue.controlFeedback() ? 'visible' : ''}`}
              aria-live="polite"
              data-testid={TEST_IDS.controlFeedbackSlot}
            >
              <Show when={venue.controlFeedback()}>
                {(feedback) => (
                  <span class={`control-feedback control-feedback-${feedback().phase}`} role="status">
                    <i /> {feedback().phase === 'pending' ? 'Applying…' : feedback().phase === 'applied' ? 'Applied' : feedback().message || 'Not applied'}
                  </span>
                )}
              </Show>
            </span>
            {/*
              * The route is worth naming only while it is a direct one — the controller's own console
              * over a cable, or Bluetooth. Cloud is simply how the console works, and saying so in the
              * header every time would be chrome carrying a fact nobody acts on.
              */}
            <Show when={venue.controlTransport() === 'lan' || venue.controlTransport() === 'bluetooth'}>
              <span class="connected-via" data-testid={TEST_IDS.connectionState}>Connected via: <strong>{connectionLabel()}</strong></span>
              <button class="context-text-action" onClick={() => venue.controlTransport() === 'lan' ? venue.disconnectLan() : void venue.disconnectBluetooth()}>Disconnect</button>
            </Show>
            {/*
              * A stream driving this room is the one fact that outranks everything else in the bar: while
              * it is up, the panel and score controls below do not reach the wire. It therefore sits with
              * Blackout — the other control that acts on the whole room — and is in the shell, so it is
              * the same line on every screen rather than a thing each screen remembers to show.
              */}
            <Show when={venue.controlOwnership()}>
              <span class="context-stream" data-testid={TEST_IDS.streamingIndicator} role="status">
                <i />
                <span class="context-stream-line"><strong>Live from Nuance</strong> · since {streamSince()}</span>
                {/* Everyone sees THAT a stream is up; only someone who may stop one gets the action. */}
                <Show when={can(venue.role(), 'stream.disconnect')}>
                  <button class="context-text-action" data-testid={TEST_IDS.streamingDisconnect} onClick={() => setStreamConfirmOpen(true)}>Disconnect stream</button>
                </Show>
              </span>
            </Show>
            <Show when={venue.outputView.remaining()}>{(remaining) => (
              <OutputViewIndicator remaining={remaining()} testId={TEST_IDS.outputViewContextIndicator} />
            )}</Show>
            <Show when={!venue.rigUnassigned()}>
              <BusyButton
                busyKey="global-blackout"
                class={`button context-blackout ${blackout() ? 'blackout-active' : ''}`}
                data-testid={TEST_IDS.globalBlackout}
                disabled={!venue.controlAvailable()}
                onClick={() => blackout() ? void setBlackout(false) : setBlackoutConfirmOpen(true)}
              ><Ban size={16} /> {blackout() ? 'Blackout on — turn off' : 'Blackout'}</BusyButton>
            </Show>
          </div>
        </header>
        <div class="page" data-screen={venue.route()}>
          <div class="page-name-mobile"><Dynamic component={active()?.icon} size={18} /><span>{active()?.label}</span></div>
          <Show when={activityText()}>
            <div class={`operator-activity ${venue.activity()?.phase === 'editing' ? 'editing' : ''}`} role="status" data-testid={TEST_IDS.operatorActivity}>
              <i /> {activityText()}
            </div>
          </Show>
          {props.children}
        </div>
      </section>

      <ConfirmModal
        open={blackoutConfirmOpen()}
        title="Blackout the room?"
        detail="Blackout sets every light in the room to 0 and keeps it there until Blackout is turned off. Continue?"
        confirmLabel="Yes"
        busyKey="global-blackout"
        onConfirm={() => void setBlackout(true)}
        onCancel={() => setBlackoutConfirmOpen(false)}
      />

      <ConfirmModal
        open={streamConfirmOpen()}
        title="Disconnect the Nuance stream?"
        detail="The room returns to the Scenes page look."
        confirmLabel="Disconnect stream"
        busyKey="disconnect-stream"
        onConfirm={() => void disconnectStream()}
        onCancel={() => setStreamConfirmOpen(false)}
      />

      <ConfirmModal
        open={Boolean(venue.takeoverConfirmation())}
        title={venue.takeoverConfirmation()?.title ?? ''}
        detail={venue.takeoverConfirmation()?.detail ?? ''}
        confirmLabel={venue.takeoverConfirmation()?.confirmLabel ?? 'Take over'}
        onConfirm={() => venue.resolveTakeoverConfirmation(true)}
        onCancel={() => venue.resolveTakeoverConfirmation(false)}
      />

      <nav class="mobile-dock" aria-label="Primary screens">
        <For each={navigation.filter((item) => item.primary)}>
          {(item) => (
            <button classList={{ active: venue.route() === item.route }} onClick={() => go(item.route)}>
              <item.icon size={20} /><span>{item.label}</span>
            </button>
          )}
        </For>
        <button classList={{ active: !navigation.find((item) => item.route === venue.route())?.primary }} onClick={() => setMobileMenu(true)}>
          <LayoutDashboard size={20} /><span>More</span>
        </button>
      </nav>

      <Show when={mobileMenu()}>
        <div class="mobile-sheet-backdrop" onMouseDown={(event) => event.target === event.currentTarget && setMobileMenu(false)}>
          <section class="mobile-sheet" role="dialog" aria-modal="true" aria-label="All screens">
            <header><strong>All screens</strong><button class="icon-button" aria-label="Close" onClick={() => setMobileMenu(false)}><X size={20} /></button></header>
            <div class="mobile-screen-grid">
              <For each={navigation}>
                {(item) => <button classList={{ active: venue.route() === item.route }} onClick={() => go(item.route)}><item.icon size={22} /><span>{item.label}</span></button>}
              </For>
            </div>
          </section>
        </div>
      </Show>

      <ToastStack />
    </div>
  );
}
